import { LayoutGrid, ClipboardList, ChefHat, Receipt, QrCode, CalendarDays, Boxes, Users, BarChart3, Mic, Printer, Smartphone } from "lucide-react";
import { useT } from "@/lib/i18n";

const GROUPS = [
  { icon: LayoutGrid, it: "Sala & tavoli", en: "Floor & tables", items: [
    ["Planimetria interattiva con tavoli trascinabili e sale multiple", "Interactive floor plan with draggable tables and multiple rooms"],
    ["Stato tavolo in tempo reale: libero, occupato, in attesa del conto", "Live table status: free, seated, waiting for the bill"],
    ["Unione tavoli, coperti e assegnazione cameriere", "Merge tables, covers and waiter assignment"],
  ]},
  { icon: ClipboardList, it: "Comande", en: "Orders", items: [
    ["Comanda digitale da tablet o telefono con varianti, extra e note", "Digital order from tablet or phone with variants, extras and notes"],
    ["Piatto per coperto: ogni portata al posto giusto", "Dish per seat: every course to the right guest"],
    ["Modifica, aggiunta e annullamento piatti anche a comanda avviata", "Edit, add or cancel dishes even after sending"],
    ["Invio a cucina e bar con un tocco, uscite a portate", "One-tap send to kitchen and bar, course-by-course firing"],
  ]},
  { icon: ChefHat, it: "Cucina & bar", en: "Kitchen & bar", items: [
    ["Monitor cucina in tempo reale con tempi e priorità colorate", "Real-time kitchen display with timers and colour priorities"],
    ["Foto del piatto sulla comanda per un impiattamento perfetto", "Dish photo on the ticket for perfect plating"],
    ["Avviso vocale e sonoro a ogni nuova comanda", "Voice and sound alert on every new ticket"],
    ["Stampa comande su stampanti termiche di rete", "Ticket printing on network thermal printers"],
  ]},
  { icon: Receipt, it: "Cassa & fiscale", en: "Cashier & fiscal", items: [
    ["Preconto, conto diviso per posto o alla romana, pagamenti misti", "Pre-bill, split by seat or evenly, mixed payments"],
    ["Scontrini: solo gestionale, stampante fiscale/RT o trasmissione online AdE", "Receipts: management only, fiscal printer/RT or online tax agency"],
    ["Fatture elettroniche, registro fatture, resi e storni", "E-invoices, invoice register, refunds and voids"],
    ["Chiusura giornaliera con riepilogo incassi e IVA", "Daily closing with revenue and VAT summary"],
  ]},
  { icon: QrCode, it: "Menù QR & ordini al tavolo", en: "QR menu & table ordering", items: [
    ["Menù digitale con foto, descrizioni, menù del giorno e prezzi sempre aggiornati", "Digital menu with photos, descriptions, specials and live prices"],
    ["14 allergeni UE con legenda e filtro per il cliente", "14 EU allergens with legend and customer filter"],
    ["Il cliente ordina dal tavolo, chiama il cameriere o chiede il conto", "Guests order from the table, call the waiter or ask for the bill"],
    ["QR stampabili per ogni tavolo e per il menù", "Printable QR codes for every table and the menu"],
  ]},
  { icon: CalendarDays, it: "Prenotazioni", en: "Reservations", items: [
    ["Pagina pubblica di prenotazione collegata al tuo sito", "Public booking page linked to your website"],
    ["Promemoria email automatici e gestione no-show", "Automatic email reminders and no-show handling"],
    ["Agenda giornaliera con assegnazione tavolo", "Daily agenda with table assignment"],
  ]},
  { icon: Boxes, it: "Magazzino & food cost", en: "Inventory & food cost", items: [
    ["Ingredienti, giacenze e scarico automatico a ogni vendita", "Ingredients, stock and automatic deduction on every sale"],
    ["Ricette per piatto con costo e margine calcolati", "Recipes per dish with calculated cost and margin"],
    ["Avvisi sotto scorta e obiettivo food cost", "Low-stock alerts and food cost target"],
  ]},
  { icon: Users, it: "Personale", en: "Staff", items: [
    ["Turni settimanali, timbrature e richieste di cambio", "Weekly rosters, clock in/out and shift change requests"],
    ["Accesso con codice o badge QR stampabile per ogni collaboratore", "Access by code or printable QR badge for each staff member"],
    ["Ruoli e permessi, registro attività di chi fa cosa", "Roles and permissions, activity log of who does what"],
  ]},
  { icon: BarChart3, it: "Report & statistiche", en: "Reports & analytics", items: [
    ["Report mensile incassi, piatti più venduti, margini", "Monthly revenue report, best sellers, margins"],
    ["Andamento per giorno, servizio e metodo di pagamento", "Trends by day, service and payment method"],
  ]},
  { icon: Mic, it: "Assistente vocale", en: "Voice assistant", items: [
    ["\"Ehi Menù\": comande e azioni a voce, mani libere", "\"Hey Menu\": hands-free voice orders and actions"],
  ]},
  { icon: Printer, it: "Stampa", en: "Printing", items: [
    ["Agente di stampa locale per stampanti termiche ESC/POS e fiscali (Epson, RCH, Ditron)", "Local print agent for ESC/POS thermal and fiscal printers (Epson, RCH, Ditron)"],
  ]},
  { icon: Smartphone, it: "Ovunque", en: "Everywhere", items: [
    ["App installabile (PWA) su tablet, PC e smartphone, interfaccia IT/EN", "Installable app (PWA) on tablets, PCs and phones, IT/EN interface"],
    ["Multi-ristorante con un solo account, tutto sincronizzato in tempo reale", "Multi-restaurant with one account, everything synced in real time"],
  ]},
];

export function LandingCapabilities() {
  const { t, lang } = useT();
  return (
    <section id="capabilities" data-testid="capabilities-section" className="bg-[#0f1f14] py-24 text-white">
      <div className="mx-auto max-w-6xl px-6">
        <p className="text-sm font-semibold text-[#8fd39c]">{t("cap_kicker")}</p>
        <h2 className="mt-2 max-w-3xl font-heading text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">{t("cap_title")}</h2>
        <p className="mt-4 max-w-2xl text-lg text-white/70">{t("cap_sub")}</p>
        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {GROUPS.map((g, idx) => { const Icon = g.icon; return (
            <div key={g.it} data-testid={`cap-group-${idx}`} className="group rounded-3xl border border-white/10 bg-white/[0.04] p-6 transition-colors hover:border-[#5aa469]/60 hover:bg-white/[0.07]">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#5aa469]/20 text-[#8fd39c] transition-transform group-hover:scale-110"><Icon size={22} /></span>
                <h3 className="font-heading text-lg font-bold">{lang === "en" ? g.en : g.it}</h3>
              </div>
              <ul className="mt-4 space-y-2.5">
                {g.items.map(([it, en]) => <li key={it} className="flex items-start gap-2 text-sm leading-relaxed text-white/80"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#8fd39c]" />{lang === "en" ? en : it}</li>)}
              </ul>
            </div>); })}
        </div>
        <p className="mt-10 text-center text-sm text-white/50">{t("cap_footer")}</p>
      </div>
    </section>
  );
}
